import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spin } from 'antd';
import {
  PlayCircleFilled,
  ReloadOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { useTranslation } from '@refinedev/core';
import { getFileUrl } from '@/Helpers';

/**
 * How long a video may sit in a loading state before we surface the
 * "this is taking unusually long" hint and the open-in-new-tab escape hatch.
 */
const SLOW_LOAD_THRESHOLD_MS = 10_000;

type PlaybackState = 'idle' | 'loading' | 'ready' | 'error';

interface VideoPlayerProps {
  /** `upload.id` of the video to play. */
  uploadId: number | string;
  /** Shown on the click-to-load placeholder. */
  fileName?: string;
  poster?: string;
  /**
   * Primary content on the page: mount the element immediately and let it
   * fetch metadata. Secondary/grid videos stay idle until clicked so a page
   * with N videos does not trigger N downloads on render.
   */
  eager?: boolean;
  className?: string;
}

export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  uploadId,
  fileName,
  poster,
  eager = false,
  className,
}) => {
  const { translate } = useTranslation();

  const [isMounted, setIsMounted] = useState(eager);
  const [state, setState] = useState<PlaybackState>(eager ? 'loading' : 'idle');
  const [isSlow, setIsSlow] = useState(false);
  // Remounts the <video> element on retry so the browser restarts the request
  // instead of reusing its failed internal media state.
  const [attempt, setAttempt] = useState(0);

  const src = getFileUrl(String(uploadId));

  // Surface a warning when a load drags on, rather than leaving the admin
  // staring at a spinner that never resolves.
  useEffect(() => {
    if (state !== 'loading') {
      setIsSlow(false);
      return;
    }
    const timer = window.setTimeout(
      () => setIsSlow(true),
      SLOW_LOAD_THRESHOLD_MS
    );
    return () => window.clearTimeout(timer);
  }, [state, attempt]);

  const handleLoad = useCallback(() => {
    setIsMounted(true);
    setState('loading');
  }, []);

  const handleRetry = useCallback(() => {
    setAttempt((n) => n + 1);
    setState('loading');
    setIsMounted(true);
  }, []);

  if (!isMounted) {
    return (
      <button
        type="button"
        onClick={handleLoad}
        aria-label={translate('videoPlayer.load')}
        className={`flex flex-col items-center justify-center gap-2 w-full bg-gray-100 dark:bg-gray-800 border-0 cursor-pointer ${className ?? 'h-48'}`}
      >
        <PlayCircleFilled className="text-4xl text-secondary" />
        <span className="text-xs text-gray-500 truncate max-w-[90%]">
          {fileName ?? translate('videoPlayer.load')}
        </span>
      </button>
    );
  }

  if (state === 'error') {
    return (
      <Alert
        type="error"
        message={translate('messages.missing-media')}
        action={
          <Button size="small" icon={<ReloadOutlined />} onClick={handleRetry}>
            {translate('videoPlayer.retry')}
          </Button>
        }
      />
    );
  }

  return (
    <div className="relative">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        key={attempt}
        src={src}
        poster={poster}
        controls
        playsInline
        autoPlay={!eager}
        // Nothing is fetched before this element mounts: non-eager videos stay
        // behind the click-to-load placeholder, so a page with N videos issues
        // zero requests on render. Once mounted the admin has asked for it, so
        // a click-to-load video may buffer freely.
        preload={eager ? 'metadata' : 'auto'}
        className={className ?? 'object-cover h-48 w-full'}
        onLoadStart={() => setState('loading')}
        onWaiting={() => setState('loading')}
        onCanPlay={() => setState('ready')}
        onPlaying={() => setState('ready')}
        onError={() => setState('error')}
      >
        {translate('videoPlayer.unsupported')}
      </video>

      {state === 'loading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/40 pointer-events-none">
          <Spin />
          <span className="text-xs text-white">
            {translate('videoPlayer.loading')}
          </span>
        </div>
      )}

      {isSlow && state === 'loading' && (
        <Alert
          className="mt-2"
          type="warning"
          showIcon
          message={translate('videoPlayer.slow')}
          action={
            <a href={src} target="_blank" rel="noreferrer">
              <Button size="small" icon={<ExportOutlined />}>
                {translate('videoPlayer.openInNewTab')}
              </Button>
            </a>
          }
        />
      )}
    </div>
  );
};

export default VideoPlayer;
